import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { DEFAULT_MOODS, type Mood } from "@instead/shared";
import { api } from "../lib/api.ts";
import { moodLabel, useT } from "../lib/i18n.ts";
import { goBackTo } from "../lib/nav.ts";
import { useReorder } from "../components/DealCard.tsx";
import { Ic } from "../components/Icons.tsx";
import { Dialog, InlineName, Toast, useToast } from "../components/Ui.tsx";

/** "Feels like" is the user's own vocabulary (round 2). Round 5: rename in
 *  place, add by typing into a row, drag to reorder, Reset to defaults when
 *  the six built-ins have been changed. */
export function Moods() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { back?: string };
  const { data } = useQuery({ queryKey: ["moods"], queryFn: api.moods.list });
  const moods = data?.moods ?? [];
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<Mood | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [toast, say] = useToast();
  const refresh = async () => { await qc.invalidateQueries({ queryKey: ["moods"] }); await qc.invalidateQueries({ queryKey: ["items"] }); };
  const [order, setOrder] = useState<string[] | null>(null);
  const ids = order ?? moods.map((m) => m.id);
  const shown = ids.map((id) => moods.find((m) => m.id === id)).filter((m): m is Mood => !!m);
  const { move: moveTo, handleProps, cardProps } = useReorder(ids, setOrder, (next) => void persistOrder(next));

  async function persistOrder(next: string[]) {
    const slots = moods.map((m) => m.sortOrder).sort((a, b) => a - b);
    const writes = next.map((id, i) => ({ id, sortOrder: slots[i]! })).filter((w) => moods.find((m) => m.id === w.id)?.sortOrder !== w.sortOrder);
    for (const w of writes) await api.moods.update(w.id, { sortOrder: w.sortOrder });
    if (writes.length) await qc.invalidateQueries({ queryKey: ["moods"] });
    setOrder(null);
  }

  async function rename(m: Mood, name: string) {
    setEditing(null);
    if (!name.trim() || name.trim() === moodLabel(m)) return;
    await api.moods.update(m.id, { name: name.trim() });
    await refresh();
    say(t("saved"));
  }

  async function create(name: string) {
    setAdding(false);
    if (!name.trim()) return;
    await api.moods.create(name.trim());
    await refresh();
    say(t("saved"));
  }

  // anything off the seed: a built-in missing, renamed, or out of seed order
  const builtIns = moods.filter((m) => m.builtIn);
  const changed =
    builtIns.map((m) => m.key).join(",") !== DEFAULT_MOODS.map((d) => d.key).join(",") ||
    builtIns.some((m) => DEFAULT_MOODS.find((d) => d.key === m.key)?.name !== m.name);

  async function reset() {
    setConfirmReset(false);
    await api.moods.reset();
    await refresh();
    say(t("saved"));
  }

  return (
    <div>
      <div className="screen-head">
        <button className="icon-btn" aria-label={t("back")} onClick={() => void goBackTo(nav, search.back, "/settings/shelf")}>{Ic.back()}</button>
        <h1 className="title">{t("moods.title")}</h1>
        <button className="btn btn-primary" style={{ minHeight: 36 }} onClick={() => setAdding(true)}>{Ic.plus({ size: 14 })} {t("moods.new")}</button>
      </div>
      {shown.map((m) => (
        <div key={m.id} className="nav-row" style={{ cursor: "default" }} {...cardProps(m.id)}>
          <button type="button" className="icon-btn drag-handle" aria-label={t("deal.reorder")} title={t("deal.reorderHint")} {...handleProps(m.id, moodLabel(m))}
            onKeyDown={(e) => {
              if (e.key === "ArrowUp") { e.preventDefault(); moveTo(m.id, -1); }
              if (e.key === "ArrowDown") { e.preventDefault(); moveTo(m.id, 1); }
            }}>{Ic.drag({ size: 16 })}</button>
          {editing === m.id ? (
            <InlineName label={t("moods.name")} value={moodLabel(m)} onDone={(v) => rename(m, v)} />
          ) : (
            <>
              <span className="chip chip-on" style={{ pointerEvents: "none" }}>{moodLabel(m)}</span>
              <button className="icon-btn" style={{ minWidth: 32, minHeight: 32, padding: 0 }} aria-label={`${t("edit")} ${moodLabel(m)}`} onClick={() => setEditing(m.id)}>{Ic.edit({ size: 14 })}</button>
              <span className="grow" />
              <button className="icon-btn danger" aria-label={`${t("remove")} ${moodLabel(m)}`} onClick={() => setConfirm(m)}>{Ic.x({ size: 14 })}</button>
            </>
          )}
        </div>
      ))}
      {adding && (
        <div className="nav-row" style={{ cursor: "default" }}>
          <InlineName label={t("moods.name")} value="" placeholder={t("moods.new")} onDone={create} />
        </div>
      )}

      {changed && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--sp-5)" }}>
          <button className="icon-btn" onClick={() => setConfirmReset(true)}>{Ic.refresh({ size: 14 })} {t("cats.reset")}</button>
        </div>
      )}

      {confirm && (
        <Dialog title={t("moods.deleteQ", { name: moodLabel(confirm) })} onClose={() => setConfirm(null)}>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setConfirm(null)}>{t("keep")}</button>
            <button className="btn btn-danger" onClick={async () => { await api.moods.remove(confirm.id); setConfirm(null); await refresh(); }}>{t("remove")}</button>
          </div>
        </Dialog>
      )}
      {confirmReset && (
        <Dialog title={t("moods.resetQ")} onClose={() => setConfirmReset(false)}>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setConfirmReset(false)}>{t("cancel")}</button>
            <button className="btn btn-primary" onClick={reset}>{t("cats.reset")}</button>
          </div>
        </Dialog>
      )}
      <Toast msg={toast} />
    </div>
  );
}
